import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useUpdateDatasheet,
  type DatasheetDetail, type SizeSlot, type UpdateDatasheetBody,
} from "@/lib/datasheets";

/// Renders only the *named* slots in the read view. A sheet using two sizes should not show five
/// empty rows — that is exactly the noise the legacy 7-column form produced. Editing exposes all
/// seven slots so a previously-blank one can be given a description and become active.
export function SizesSection({ sheet }: { sheet: DatasheetDetail }) {
  const { has } = usePermissions();
  const update = useUpdateDatasheet(sheet.datasheetId);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<SizeSlot[]>(sheet.sizes);
  const [saving, setSaving] = useState(false);

  const setRow = (i: number, patch: Partial<SizeSlot>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  async function save() {
    setSaving(true);
    try {
      const body: UpdateDatasheetBody = { sizes: rows };
      await update.mutateAsync({ body, rowVersion: sheet.rowVersionBase64 });
      toast.success("Sizes saved");
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 409
        ? "Changed since you opened it — reload."
        : "Could not save the sizes");
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    const named = sheet.sizes.filter((s) => s.desc);

    return (
      <div className="space-y-3">
        {named.length === 0 ? (
          <p className="text-sm text-muted-foreground">No size variants defined yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">#</th>
                <th>Description</th>
                <th className="text-right">Available</th>
                <th className="text-right">Unit price ex GST</th>
              </tr>
            </thead>
            <tbody>
              {named.map((s) => (
                <tr key={s.index} className="border-b last:border-0">
                  <td className="py-2">{s.index}</td>
                  <td>{s.desc}</td>
                  <td className={`text-right ${s.quantityAvail < 0 ? "text-destructive font-medium" : ""}`}>
                    {s.quantityAvail}
                  </td>
                  <td className="text-right">
                    {s.unitPriceExGst === null ? "—" : `$${s.unitPriceExGst.toFixed(2)}`}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t">
                <td colSpan={2} className="py-2">Delivery charges available</td>
                <td className="text-right">{sheet.deliveryChargesAvail}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={2} className="py-2">Fittings available</td>
                <td className="text-right">{sheet.prepaidFittingQuantityAvail}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
        {has("datasheet:write") ? (
          <Button size="sm" variant="outline" onClick={() => { setRows(sheet.sizes); setEditing(true); }}>
            Edit sizes
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Editing a stored quantity here does not create a ledger entry — if it no longer matches the
        purchase/usage history you will see a drift warning. Record an adjustment instead to keep the
        ledger in step.
      </p>
      {rows.map((r, i) => (
        <div key={r.index} className="flex items-center gap-2">
          <span className="w-8 text-sm text-muted-foreground">{r.index}</span>
          <Input
            aria-label={`Size ${r.index} description`}
            placeholder="Description"
            value={r.desc ?? ""}
            onChange={(e) => setRow(i, { desc: e.target.value || null })}
          />
          <Input
            aria-label={`${r.desc ?? `Size ${r.index}`} quantity`}
            type="number"
            className="w-24"
            value={r.quantityAvail}
            onChange={(e) => setRow(i, { quantityAvail: Number(e.target.value) || 0 })}
          />
          <Input
            aria-label={`Size ${r.index} price`}
            type="number"
            className="w-28"
            value={r.unitPriceExGst ?? ""}
            onChange={(e) => setRow(i, { unitPriceExGst: e.target.value === "" ? null : Number(e.target.value) })}
          />
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void save()} disabled={saving}>Save</Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}
